// Tiny i18n accessor for the QR Phone caller flow and staff softphone.
//
// The whole app imports `t` from here and reads strings as `t.<key>` (calling the
// function-valued ones, e.g. `t.queuePosition(3)`). Today there is a single language
// (English); full multi-language i18n is intentionally postponed. The structure is
// ready for it, though, so adding a language stays cheap.
//
// ── How to add another language (e.g. Spanish / French) later ────────────────────
// 1. Copy `en.ts` to `es.ts` (or `fr.ts`) and translate the VALUES only — keep every
//    key and every function signature identical.
// 2. Register it in the `dictionaries` map below and add its backend language code to
//    `BACKEND_LANGUAGE_CODES`.
// 3. Set `currentLanguage` (today a constant; later it can be driven per user — from
//    the QR token, the browser locale, a staff preference, etc.).
// No component or screen needs to change: they already reference `t.<key>`.
import { en } from './en'
import type { Dictionary } from './en'

export type { Dictionary } from './en'

export type Language = 'en' // | 'es' | 'fr'  ← extend the union when adding languages

// Register every available dictionary here. Add `es: es` / `fr: fr` once translated.
const dictionaries: Record<Language, Dictionary> = {
  en,
}

// Backend message language codes (the API localises some responses by this code).
// Keep them next to the dictionaries so a language is configured in ONE place.
// Codes are defined by the backend: 1 = English, 3 = Spanish.
export const BACKEND_LANGUAGE_CODES: Record<Language, number> = {
  en: 1,
  // es: 3,
}

// The active language. Single constant for now; this is the one obvious switch point
// to make the language per-user later (no re-extraction needed).
export const currentLanguage: Language = 'en'

// The active dictionary, consumed across the app as `t.<key>`.
export const t: Dictionary = dictionaries[currentLanguage]

// The active backend language code, consumed by the core HTTP layer / globals so
// backend-localised messages arrive in the same language as the UI.
export const backendLanguageCode: number = BACKEND_LANGUAGE_CODES[currentLanguage]
